import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, errDetail, fmtIST } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { RefreshCcw, CheckCircle2, XCircle, AlertTriangle, PhoneCall, Copy, Info } from "lucide-react";

// R14: a denied request (403) and an unreachable server are different problems with different fixes - never
// shown as a generic failure and never as "no anomalies".
export function racePreviewErrorMessage(e, fallback) {
  if (e?.incompatible) return e.message;
  const status = e?.response?.status;
  if (status === 403) return "You do not have permission to run the historical Voice preview. Ask an administrator for access.";
  if (!e?.response) return "Could not reach the server, so the historical Voice preview was not loaded. Check the connection and try again.";
  return fallback;
}

export function validateRacePreview(data) {
  const object = (v) => v && typeof v === "object" && !Array.isArray(v);
  if (!object(data?.summary) || !object(data?.conditions) ||
      Object.values(data.summary).some((s) => !object(s) || !Number.isFinite(s.event_count)) ||
      Object.values(data.conditions).some((c) => !object(c) || !Array.isArray(c.findings) ||
        c.findings.some((g) => !object(g) || !Array.isArray(g.matching_attempts) ||
          g.matching_attempts.some((a) => !object(a))))) {
    const err = new Error("Historical Voice preview returned an incompatible report. Findings were not displayed; please report this error.");
    err.incompatible = true;
    throw err;
  }
  return data;
}

const copyText = async (text, what) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${what} copied`);
  } catch {
    toast.error("Could not copy — select and copy manually");
  }
};

/* 2026-09 Voice consolidation — the ONE authoritative Administration page
   for all ElevenLabs / AI Voice configuration and monitoring (previously
   fragmented across Integrations, Ops Policies, and this tab's own earlier
   read-only-only version). Everything here reuses existing endpoints —
   PUT /admin/integrations/elevenlabs_voice for credentials/master-switch/
   the dedicated test-send flag (the exact same generic mechanism every
   other provider card uses), GET/PUT /admin/voice-settings for the
   voice-specific operational controls, GET /admin/voice-status for
   read-only verification/webhook health, GET /admin/voice-monitor for the
   attempt table, plus the existing test-call/controlled-test actions.
   Deliberately NOT a call-center application — no manual-retry or any
   other action here can place another call beyond the two explicit,
   single-opportunity test actions below (both is_test_record-only, never
   a batch operation, never usable on a real lead). */

const YesNo = ({ ok, label }) => (
  <div className="flex items-center gap-1.5 text-xs">
    {ok ? <CheckCircle2 className="h-3.5 w-3.5 text-[hsl(var(--success))]" />
        : <XCircle className="h-3.5 w-3.5 text-[hsl(var(--destructive))]" />}
    <span>{label}</span>
  </div>
);

const EMPTY_TEST_CALL_FORM = {
  phone: "", name: "", email: "", city: "", product_code: "",
  lead_source: "", lead_campaign: "", lead_details: "", scenario_label: "",
};

// The truly Voice-specific subset of /admin/voice-settings' VOICE_KEYS —
// chase.call_window / booking.self_booking_wait_minutes / booking.* /
// reminders.* are genuinely SHARED operational settings (Finance payment
// reminders and ordinary booking automations also consume them) and stay
// editable ONLY in Administration -> Ops policies; this page shows their
// current values read-only (via voice-status) rather than creating a
// second editable copy of them.
const VOICE_ONLY_KEYS = [
  ["chase.voice_attempts", "Max ordinary attempts", "voice-oc-attempts"],
  ["chase.voice_retry_gap_minutes", "Ordinary retry delay (minutes)", "voice-oc-gap"],
  ["chase.voice_max_concurrent", "Max concurrent Voice calls", "voice-oc-concurrent"],
  ["chase.voice_stale_timeout_minutes", "Stale lease timeout (minutes)", "voice-oc-stale"],
  ["chase.voice_capacity_wait_minutes", "Capacity-wait requeue (minutes)", "voice-oc-capwait"],
  ["chase.voice_min_meaningful_duration_secs", "Min. meaningful call duration (seconds)", "voice-oc-minduration"],
  ["chase.voice_pre_dispatch_max_retries", "Pre-dispatch internal retry limit", "voice-oc-predispatch-retries"],
  ["chase.voice_pre_dispatch_retry_minutes", "Pre-dispatch retry delay (minutes)", "voice-oc-predispatch-minutes"],
];

// 2026-09 fix: the backend (routers_ops._mask, used by GET
// /admin/integrations for every "secret" field) already reveals at most
// the last 4 characters server-side — that's the ONE and ONLY masking
// layer; the full credential is never returned once saved. A
// type="password" input here was WRONG: it re-masks every character the
// browser renders, including the already-safe "••••c282" fingerprint
// string itself, defeating the whole point of showing it. Plain text is
// exactly what the sibling ProviderCard (IntegrationsAdmin.jsx) already
// does for every other provider's secret fields, for the same reason.
// 2026-09 Voice dispatch audit — human-readable labels for the new
// dispatch_state/failure_stage diagnostic fields, matching exactly the
// values scheduler.py/adapters.py actually write.
export const DISPATCH_STATE_LABELS = {
  waiting_capacity: "Waiting for capacity (1 slot in use)",
  waiting_calling_window: "Waiting for the calling window to reopen (no capacity lease held)",
  lease_acquired: "Capacity lease acquired",
  dispatch_started: "Dispatch started (contacting provider)",
  provider_dispatched: "Provider dispatched — call placed",
  provider_rejected: "Provider rejected the request",
  pre_dispatch_failed: "Pre-dispatch failure — retrying automatically",
  pre_dispatch_exhausted: "Pre-dispatch failure — retries exhausted, needs review",
  stale_recovered: "Recovered by stale sweep (dispatched, never resolved)",
  recovered_requeued: "Recovered — requeued after confirmed never-dispatched bug",
  // 2026-09-II audit — Category B: provider genuinely accepted the
  // dispatch (or acceptance itself is unconfirmed) but no post-call
  // result ever arrived within the stale timeout.
  provider_result_missing: "Provider accepted, but no final call result ever arrived",
};
const FAILURE_STAGE_LABELS = {
  pre_dispatch_internal: "Pre-dispatch — never reached the provider (SalesOS-side failure)",
  provider_rejected: "Provider rejected — a real response was received",
  stale_recovery: "Stale recovery — dispatched but never resolved",
  post_dispatch_no_result: "Post-dispatch — provider accepted, final result never arrived",
};
const STALE_CLASSIFICATION_LABELS = {
  resolved: "Resolved — not currently stale-eligible",
  not_stale_eligible: "Not stale-eligible (not in queued/initiated/initiation_uncertain)",
  waiting_within_timeout: "Within the stale timeout — not yet eligible for recovery",
  unknown: "Unknown (no created_at on record)",
};
// Round-12 Workstream A — human labels for voice_race_audit's 9 condition
// keys, matching the exact wording in voice_race_audit.py's own docstring.
const CONDITION_LABELS = {
  handover_then_later_dispatch: "Handover then a later provider dispatch (the Pratik Saini shape)",
  resolved_still_ai_calling: "Resolved attempt still showing \"AI calling\"",
  voice_in_progress_no_live_lease: "\"Voice In Progress\" without a live lease",
  expired_lease_holding_capacity: "Expired lease still holding capacity",
  result_missing_ownership_active: "Result missing while AI ownership stayed active",
  multiple_active_review_tasks: "Multiple active human-review tasks, same purpose",
  dispatch_after_generation_superseded: "Dispatch after its generation was superseded",
  more_than_three_genuine_attempts: "More than 3 genuine attempts",
  attempt_count_consumed_by_technical_failure: "Attempt count consumed by a technical failure",
};

export const staleClassificationLabel = (c) => {
  if (!c) return "—";
  if (STALE_CLASSIFICATION_LABELS[c]) return STALE_CLASSIFICATION_LABELS[c];
  if (c.startsWith("eligible_never_dispatched"))
    return "Eligible now — will be recovered as never-dispatched (no attempt consumed)";
  if (c.startsWith("waiting_calling_window")) {
    const m = c.match(/reopens ([^;)]+)/);
    return `Waiting for the calling window to reopen${m ? ` at ${fmtIST(m[1], { zone: true, year: true })}` : ""}. A legitimate wait; holds no capacity lease and consumes no attempt.`;
  }
  if (c.startsWith("waiting_capacity")) {
    const m = c.match(/not eligible until ([^;)]+)/);
    return `Waiting for capacity${m ? ` — not eligible for recovery until ${fmtIST(m[1], { zone: true, year: true })}` : ""}. A legitimate wait; consumes no attempt.`;
  }
  if (c.startsWith("eligible_provider_result_missing"))
    return "Eligible now — a last provider-status check runs first; if still unknown it is recorded as result-unknown (counts as one attempt) and a due review task is created";
  return c;
};

// R16 N1 / N6 - one honest line for each dispatch-time diagnostic: counts and causes only (never an answer), and an explicit "not recorded" for an attempt dispatched before they existed.
// R17: "no context recorded" has FOUR different causes and the old line gave one of them to all: a newly queued attempt that has not been dispatched yet showed "dispatched before this was kept".
const NOT_PREPARED_STATES = ["waiting_calling_window", "waiting_capacity", "lease_acquired", "dispatch_started", "provider_dispatch_claimed"];
const NEVER_REACHED_STATES = ["superseded_before_dispatch", "pre_dispatch_failed", "pre_dispatch_exhausted"];
export const absentContextLabel = (a) => {
  if (!a) return "Not recorded";
  const ds = a.dispatch_state;
  if (NOT_PREPARED_STATES.includes(ds) || (!ds && a.outcome === "queued" && !a.provider_dispatched_at && !a.initiated_at))
    return "Not prepared yet - this attempt has not been dispatched; the context is built at the moment it is sent to the provider";
  if (NEVER_REACHED_STATES.includes(ds)) return `Not built - this attempt never reached the provider (${String(ds).replaceAll("_", " ")})`;
  if (a.provider_dispatched_at || ["provider_dispatched", "provider_result_missing"].includes(ds))
    return "Not recorded (dispatched before this was kept)";
  return "Not recorded";
};
export const leadContextLabel = (c, attempt) => {
  if (!c) return absentContextLabel(attempt);
  if (c.state === "error") return `Could not be built (${c.error || "error"}) - the call went ahead with the structured fields only`;
  if (c.state === "empty") return "No saved enquiry answers for this pursuit - nothing was sent";
  const parts = [`${c.fields ?? 0} answers sent (${c.structured ?? 0} structured, ${c.raw_answers ?? 0} raw), ${c.chars ?? 0} characters`];
  if (c.omitted) parts.push(`${c.omitted} omitted for size`);
  if (c.excluded) parts.push(`${c.excluded} withheld (identity / credential)`);
  return parts.join("; ");
};
export const availabilityContextLabel = (c, attempt) => {
  if (!c) return absentContextLabel(attempt);
  if (c.state === "no_type") return "No appointment type resolves for this product - no availability was sent";
  if (c.state === "ok") return `Slots found within ${c.horizon_days} days (earliest ${c.earliest || "-"}); pool ${c.pool_key}, capacity ${c.capacity}`;
  const why = Object.entries(c.empty_reasons || {}).map(([k, v]) => `${k.replaceAll("_", " ")}: ${v} day${v === 1 ? "" : "s"}`).join(", ");
  return `NO slots within ${c.horizon_days} days (${why || "cause not recorded"}); pool ${c.pool_key}, capacity ${c.capacity}`;
};

const fmtDur = (secs) => {
  if (secs == null) return "—";
  if (secs < 90) return `${Math.round(secs)}s`;
  return `${Math.round(secs / 60)} min`;
};

const DetailField = ({ label, value }) => (
  <div className="flex items-start gap-2 text-xs py-1 border-b last:border-b-0">
    <span className="text-muted-foreground w-44 shrink-0">{label}</span>
    <span className="font-mono break-all">{value ?? "—"}</span>
  </div>
);

// Round-12 Workstream F — every operational Voice time is shown converted to
// the org timezone (Asia/Kolkata) with an explicit "IST" label; the raw
// stored UTC value is available here too, but only as secondary text in
// this audit-detail popup — never as the primary reading, and never on the
// main summary cards above.
const DetailFieldTime = ({ label, iso }) => (
  <div className="flex items-start gap-2 text-xs py-1 border-b last:border-b-0">
    <span className="text-muted-foreground w-44 shrink-0">{label}</span>
    <span className="font-mono break-all">
      {iso ? fmtIST(iso, { zone: true, year: true }) : "—"}
      {iso && <span className="text-muted-foreground ml-1.5">(UTC: {iso})</span>}
    </span>
  </div>
);

const CredentialField = ({ label, value, onChange, testId }) => (
  <div className="space-y-1.5">
    <Label className="text-xs">{label}</Label>
    <Input data-testid={testId} value={value ?? ""} onChange={onChange} className="h-8 text-sm font-mono"
           placeholder="Not set" />
  </div>
);

export const VoiceMonitorTab = () => {
  const [status, setStatus] = useState(null);
  const [monitor, setMonitor] = useState(null);
  const [provider, setProvider] = useState(null);
  const [credFields, setCredFields] = useState({});
  const [savingCreds, setSavingCreds] = useState(false);
  const [togglingMaster, setTogglingMaster] = useState(false);
  const [togglingTestSends, setTogglingTestSends] = useState(false);
  const [opControls, setOpControls] = useState(null);
  const [savingOpControls, setSavingOpControls] = useState(false);
  const [testOppId, setTestOppId] = useState("");
  const [triggering, setTriggering] = useState(false);
  const [products, setProducts] = useState([]);
  const [testCallForm, setTestCallForm] = useState(EMPTY_TEST_CALL_FORM);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [placingCall, setPlacingCall] = useState(false);
  const [testOnly, setTestOnly] = useState(false);
  const [detailAttempt, setDetailAttempt] = useState(null);
  const [recoveryPreview, setRecoveryPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [selectedRecovery, setSelectedRecovery] = useState({});
  const [executingRecovery, setExecutingRecovery] = useState(false);
  // Round-12 Workstream A — Historical Voice race preview (read-only,
  // never an apply action; see GET /admin/voice-race-preview).
  const [racePreview, setRacePreview] = useState(null);
  const [racePageBusy, setRacePageBusy] = useState({});
  const [racePageError, setRacePageError] = useState({});
  const [loadingRacePreview, setLoadingRacePreview] = useState(false);
  const [expandedFinding, setExpandedFinding] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/voice-status").then((r) => setStatus(r.data)).catch((e) => toast.error(errDetail(e)));
    api.get("/admin/voice-monitor").then((r) => setMonitor(r.data)).catch((e) => toast.error(errDetail(e)));
    api.get("/admin/integrations").then((r) => {
      const p = (r.data.providers || []).find((x) => x.provider === "elevenlabs_voice");
      setProvider(p || null);
      const v = {};
      (p?.fields || []).forEach((f) => { v[f.key] = f.value || ""; });
      setCredFields(v);
    }).catch(() => {});
    api.get("/admin/voice-settings").then((r) => setOpControls(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    load();
    api.get("/products", { params: { selectable_only: true } })
      .then((r) => setProducts(r.data || [])).catch(() => {});
  }, [load]);

  // Plain conditional-render modal below (not the shared AlertDialog
  // primitive) — that component's Radix exit-animation never fires
  // animation-fill-mode on this project's installed tailwindcss-animate
  // version, so Presence never unmounts it: Cancel/Confirm correctly flip
  // React state but the overlay stays visually stuck on screen. This gate
  // is safety-critical (it's the last confirmation before a real phone
  // call), so it gets a dependency-free implementation instead. Esc closes
  // it, matching normal dialog expectations.
  useEffect(() => {
    if (!confirmOpen) return;
    const onKey = (e) => { if (e.key === "Escape" && !placingCall) setConfirmOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [confirmOpen, placingCall]);

  const saveCredentials = async () => {
    setSavingCreds(true);
    try {
      await api.put("/admin/integrations/elevenlabs_voice", { config: credFields });
      toast.success("ElevenLabs credentials saved");
      load();
    } catch (e) { toast.error(errDetail(e)); } finally { setSavingCreds(false); }
  };

  const toggleMaster = async (enabled) => {
    setTogglingMaster(true);
    try {
      await api.put("/admin/integrations/elevenlabs_voice", { enabled });
      toast.success(enabled
        ? "Real-lead AI Voice automation ENABLED — queued attempts will start dialing"
        : "Real-lead AI Voice automation switched OFF — chases hand over to human follow-up");
      load();
    } catch (e) { toast.error(errDetail(e)); } finally { setTogglingMaster(false); }
  };

  const toggleVoiceTestSends = async (v) => {
    setTogglingTestSends(true);
    try {
      await api.put("/admin/integrations/elevenlabs_voice",
        { config: { "integrations.elevenlabs_voice.allow_test_sends": v } });
      toast.success(v ? "Voice test calls allowed" : "Voice test calls blocked");
      load();
    } catch (e) { toast.error(errDetail(e)); } finally { setTogglingTestSends(false); }
  };

  const saveOpControls = async () => {
    setSavingOpControls(true);
    try {
      await api.put("/admin/voice-settings", { settings: opControls });
      toast.success("Voice operational controls saved");
      load();
    } catch (e) { toast.error(errDetail(e)); } finally { setSavingOpControls(false); }
  };

  const triggerControlledTest = async () => {
    if (!testOppId.trim()) { toast.warning("Enter a seeded TEST opportunity ID"); return; }
    setTriggering(true);
    try {
      const { data } = await api.post("/admin/voice-controlled-test", { opportunity_id: testOppId.trim() });
      toast.success(`Controlled test call queued — attempt #${data.attempt_no}`);
      setTestOppId(""); load();
    } catch (e) { toast.error(errDetail(e)); } finally { setTriggering(false); }
  };

  const setField = (k) => (e) => setTestCallForm((f) => ({ ...f, [k]: e.target.value }));

  // 2026-09 Voice dispatch audit — bounded, admin-confirmed repair of
  // historical pre-dispatch-bug failures. Preview never mutates anything;
  // execute only ever acts on the exact rows an admin has ticked here.
  const loadRecoveryPreview = async () => {
    setLoadingPreview(true);
    try {
      const { data } = await api.get("/admin/voice-recovery/preview");
      setRecoveryPreview(data);
      setSelectedRecovery({});
    } catch (e) { toast.error(errDetail(e)); } finally { setLoadingPreview(false); }
  };

  const toggleRecoverySelection = (vid) =>
    setSelectedRecovery((s) => ({ ...s, [vid]: !s[vid] }));

  // Round-12 Workstream A — loads the read-only historical-race preview.
  // No apply action exists for this preview anywhere in the app.
  const loadRacePreview = async () => {
    setLoadingRacePreview(true);
    try {
      const { data } = await api.get("/admin/voice-race-preview");
      setRacePreview(validateRacePreview(data));
      setRacePageBusy({}); setRacePageError({});          // a fresh run always starts every category back at page 1
    } catch (e) {
      setRacePreview(null); // a failed load must never leave a previous report on screen as if current
      toast.error(racePreviewErrorMessage(e, errDetail(e)));
    } finally { setLoadingRacePreview(false); }
  };

  // R15 U3: every category has its own pager. Each category used to show only its first 50 customer groups of hundreds (738 / 687 / 746 / 133) with no way to reach the rest. The
  // endpoint pages every category with one `page` parameter, so a category's next page is requested with that page number and ONLY that category's block is replaced; the other
  // categories (and the counts in the summary) stay exactly as they were. A failed page request keeps the page that was already showing and says so - it never blanks the report.
  const loadRacePage = async (condKey, page) => {
    setRacePageBusy((b) => ({ ...b, [condKey]: true }));
    setRacePageError((er) => ({ ...er, [condKey]: null }));
    try {
      const { data } = await api.get(`/admin/voice-race-preview?page=${page}`);
      const checked = validateRacePreview(data);
      if (!checked.conditions[condKey]) throw new Error("That category was missing from the response.");
      setRacePreview((prev) => (prev ? { ...prev, conditions: { ...prev.conditions, [condKey]: checked.conditions[condKey] } } : prev));
    } catch (e) {
      setRacePageError((er) => ({ ...er, [condKey]: racePreviewErrorMessage(e, errDetail(e)) }));
    } finally { setRacePageBusy((b) => ({ ...b, [condKey]: false })); }
  };

  const executeRecovery = async () => {
    const ids = Object.entries(selectedRecovery).filter(([, v]) => v).map(([k]) => k);
    if (ids.length === 0) { toast.warning("Select at least one attempt to recover"); return; }
    setExecutingRecovery(true);
    try {
      const { data } = await api.post("/admin/voice-recovery/execute", { voice_attempt_ids: ids });
      toast.success(`${data.recovered_count} attempt(s) recovered and requeued` +
        (data.skipped_count ? `, ${data.skipped_count} skipped` : ""));
      loadRecoveryPreview();
      load();
    } catch (e) { toast.error(errDetail(e)); } finally { setExecutingRecovery(false); }
  };

  const openConfirm = () => {
    if (!testCallForm.phone.trim()) { toast.warning("Enter a test destination phone number"); return; }
    if (!testCallForm.name.trim()) { toast.warning("Enter a dummy customer name"); return; }
    if (!testCallForm.product_code) { toast.warning("Choose a product"); return; }
    setConfirmOpen(true);
  };

  const placeTestCall = async () => {
    // One id per explicit confirmation click — created here (not at dialog
    // mount/open), so a genuine double-click/network retry of THIS click
    // collapses server-side onto one created test call, while re-opening
    // the dialog for a fresh attempt (Cancel then Confirm again) always
    // gets its own new id. placingCall (disabling the button below) is the
    // first line of defence; this id is the real, server-enforced one.
    const requestId = crypto.randomUUID();
    setPlacingCall(true);
    try {
      const { data } = await api.post("/admin/voice-test-call", {
        ...testCallForm,
        email: testCallForm.email || null,
        city: testCallForm.city || null,
        lead_source: testCallForm.lead_source || null,
        lead_campaign: testCallForm.lead_campaign || null,
        lead_details: testCallForm.lead_details || null,
        scenario_label: testCallForm.scenario_label || null,
        client_request_id: requestId,
      });
      toast.success(
        `Test call queued for ${fmtIST(data.voice_attempt_scheduled_for)} — ` +
        `customer ${data.customer_id} (${data.product})`);
      setConfirmOpen(false);
      setTestCallForm(EMPTY_TEST_CALL_FORM);
      load();
    } catch (e) { toast.error(errDetail(e)); } finally { setPlacingCall(false); }
  };

  if (!status || !monitor || !provider || !opControls) return <Skeleton className="h-64" />;

  const visibleAttempts = testOnly ? monitor.attempts.filter((a) => a.is_test_record) : monitor.attempts;
  const sweepHealth = monitor.stale_sweep_health || {};
  const safeStateOk = !status.voice_master_switch_enabled && status.voice_allow_test_sends;

  return (
    <div className="space-y-4" data-testid="voice-monitor-admin">
      <Card>
        <CardHeader className="pb-2 flex-row items-center justify-between">
          <CardTitle className="text-sm font-heading">ElevenLabs provider configuration</CardTitle>
          <Button size="sm" variant="ghost" onClick={load} data-testid="voice-status-refresh"><RefreshCcw className="h-3.5 w-3.5" /></Button>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            {(provider.fields || []).map((f) => (
              <CredentialField key={f.key} label={f.label}
                testId={`voice-cred-${f.key.replace(/\./g, "-")}`}
                value={credFields[f.key]}
                onChange={(e) => setCredFields((c) => ({ ...c, [f.key]: e.target.value }))} />
            ))}
          </div>
          <div className="flex items-center justify-between">
            <Button size="sm" disabled={savingCreds} onClick={saveCredentials} data-testid="voice-cred-save">
              {savingCreds ? "Saving…" : "Save credentials"}
            </Button>
            <div className="text-xs">
              {status.elevenlabs_phone_number_id_configured && (
                status.elevenlabs_phone_number_id_matches_expected ? (
                  <span className="text-[hsl(var(--success))] flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> Phone Number ID matches expected production value</span>
                ) : (
                  <span className="text-[hsl(var(--warning))] flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" /> Does NOT match expected production value (<code className="font-mono">{status.expected_production_phone_number_id}</code>)</span>
                )
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm font-heading">Real-lead AI Voice automation</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          <div className="flex items-center gap-3">
            <Switch data-testid="voice-master-switch" checked={!!status.voice_master_switch_enabled}
                    disabled={togglingMaster} onCheckedChange={toggleMaster} />
            <Badge variant="outline" className={status.voice_master_switch_enabled
              ? "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/30"
              : "bg-muted text-muted-foreground"} data-testid="voice-master-switch-badge">
              {status.voice_master_switch_enabled ? "ON — dialing real leads" : "OFF — human follow-up only"}
            </Badge>
          </div>
          <p className="text-[11px] text-muted-foreground">When ON, the AI voice agent chases customers who haven't booked within the calling window below. When OFF, those chases hand over to human follow-up tasks instead — no calls are ever placed to real leads.</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm font-heading">Voice test controls</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          <div className="flex items-center gap-3">
            <Switch data-testid="voice-test-sends-toggle" checked={!!status.voice_allow_test_sends}
                    disabled={togglingTestSends} onCheckedChange={toggleVoiceTestSends} />
            <Badge variant="outline" className={status.voice_allow_test_sends
              ? "bg-[hsl(var(--info))]/10 text-[hsl(var(--info))] border-[hsl(var(--info))]/20"
              : "bg-muted text-muted-foreground"}>
              Voice test calls allowed: {status.voice_allow_test_sends ? "YES" : "NO"}
            </Badge>
          </div>
          <p className="text-[11px] text-muted-foreground">
            A dedicated switch (<code className="font-mono">integrations.elevenlabs_voice.allow_test_sends</code>) —
            fully independent of the generic <code className="font-mono">integrations.allow_test_sends</code> flag
            other providers (WhatsApp, Calendar, Meta, outbound webhooks) use. Turning this on can never arm an
            unrelated provider's TEST-record sends, and the generic flag being on elsewhere can never authorize a
            Voice call. The safe state for controlled testing is exactly this: real-lead automation OFF above, this
            switch ON.
          </p>
          {safeStateOk && (
            <Badge variant="outline" className="bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/30">
              Safe test state active — real-lead automation OFF, Voice test calls ON
            </Badge>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm font-heading">Voice operational controls</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid sm:grid-cols-3 gap-3">
            {VOICE_ONLY_KEYS.map(([key, label, testId]) => (
              <div key={key} className="space-y-1">
                <Label className="text-xs">{label}</Label>
                <Input data-testid={testId} type="number" min="0" className="h-9"
                       value={opControls[key] ?? ""}
                       onChange={(e) => setOpControls((o) => ({ ...o, [key]: Number(e.target.value) }))} />
              </div>
            ))}
          </div>
          <div className="rounded-md border p-2.5 text-xs space-y-1">
            <p className="text-[11px] font-medium text-muted-foreground">Shared settings (edit in Administration → Ops policies — shown here read-only since Finance reminders and ordinary booking automations use them too, not Voice alone)</p>
            <div className="flex items-center gap-2"><span className="text-muted-foreground w-40 shrink-0">Outbound calling window</span><span>{status.calling_window?.start}–{status.calling_window?.end} IST</span></div>
            <div className="flex items-center gap-2"><span className="text-muted-foreground w-40 shrink-0">First-call delay</span><span>{status.first_call_delay_minutes != null ? `${status.first_call_delay_minutes} min (self-booking wait)` : "—"}</span></div>
          </div>
          <Button size="sm" disabled={savingOpControls} onClick={saveOpControls} data-testid="voice-opcontrols-save">
            {savingOpControls ? "Saving…" : "Save operational controls"}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm font-heading">Webhook / integration health</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-1.5">
            <YesNo ok={status.elevenlabs_webhook_secret_configured} label="Webhook signing secret configured" />
            <YesNo ok={status.elevenlabs_api_key_configured} label="API key configured" />
            <YesNo ok={status.elevenlabs_agent_id_configured} label={`Agent ID (${status.elevenlabs_agent_id_masked || "—"})`} />
            <YesNo ok={status.elevenlabs_phone_number_id_configured} label={`Phone Number ID (${status.elevenlabs_phone_number_id_masked || "—"})`} />
          </div>
          <div className="rounded-md border p-2.5 text-xs space-y-1">
            <div className="flex items-center gap-2"><Label className="text-[11px] text-muted-foreground w-44 shrink-0">Webhook URL</Label><code className="font-mono truncate" data-testid="voice-status-webhook-url">{status.webhook_url}</code></div>
            <div className="flex items-center gap-2"><Label className="text-[11px] text-muted-foreground w-44 shrink-0">Last webhook processed</Label><span data-testid="voice-status-last-webhook">{status.last_successfully_processed_webhook_at ? fmtIST(status.last_successfully_processed_webhook_at, { zone: true }) : "never"}</span></div>
            <div className="flex items-center gap-2"><Label className="text-[11px] text-muted-foreground w-44 shrink-0">Last attempt resolved (any mechanism)</Label><span>{status.last_attempt_resolved_at_any_mechanism ? fmtIST(status.last_attempt_resolved_at_any_mechanism, { zone: true }) : "never"} <span className="text-muted-foreground">— includes the stale-timeout sweep, not proof a webhook arrived</span></span></div>
            <div className="flex items-center gap-2"><Label className="text-[11px] text-muted-foreground w-44 shrink-0">Unmatched webhooks (24h)</Label><span>{status.recent_webhook_error_count_24h}</span></div>
            <div className="flex items-center gap-2"><Label className="text-[11px] text-muted-foreground w-44 shrink-0">Genuine webhooks received (24h)</Label><span>{status.genuine_webhooks_received_count_24h}</span></div>
            <div className="flex items-center gap-2"><Label className="text-[11px] text-muted-foreground w-44 shrink-0">Provider result missing (24h)</Label><span>{status.provider_result_missing_count_24h}</span></div>
            {status.last_webhook_failure_reason && (
              <div className="flex items-center gap-2"><Label className="text-[11px] text-muted-foreground w-44 shrink-0">Last webhook failure</Label><span className="text-[hsl(var(--warning))]">{status.last_webhook_failure_reason} ({fmtIST(status.last_webhook_failure_at, { zone: true })})</span></div>
            )}
            <div className="flex items-center gap-2"><Label className="text-[11px] text-muted-foreground w-44 shrink-0">Call Initiation Failure</Label><span>Supported in SalesOS — provider-side event status not knowable from here</span></div>
          </div>
          {status.no_result_diagnosis && (
            <div className="rounded-md border border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/10 p-2.5 text-xs space-y-1"
                 data-testid="voice-status-no-result-diagnosis">
              <p className="font-medium text-[hsl(var(--destructive))] flex items-center gap-1.5">
                <AlertTriangle className="h-3.5 w-3.5" /> Likely ElevenLabs-side configuration problem
              </p>
              <p>{status.no_result_diagnosis}</p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm font-heading">Reference — configuring the agent in ElevenLabs</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div>
            <p className="text-xs font-medium">What SalesOS sends the agent (per call, as dynamic variables)</p>
            <p className="text-[11px] text-muted-foreground mt-1">
              <code className="font-mono">customer_name</code> (first name), <code className="font-mono">customer_full_name</code>, <code className="font-mono">customer_phone</code>, <code className="font-mono">customer_email</code>, <code className="font-mono">customer_city</code>, <code className="font-mono">lead_source</code>, <code className="font-mono">lead_campaign</code>, <code className="font-mono">lead_details</code> (any Instaform/Meta or Google Sheets custom question answers on file), <code className="font-mono">product</code>, <code className="font-mono">voice_attempt_id</code>, and <code className="font-mono">earliest_available_date</code>/<code className="font-mono">available_slots_summary</code> — real open consultation slots for the next 14 days.
            </p>
          </div>
          <div>
            <p className="text-xs font-medium">Call outcome webhook (point your ElevenLabs agent's post-call webhook here)</p>
            <div className="flex items-center gap-2 text-xs mt-1">
              <code className="font-mono bg-muted border rounded px-1.5 py-0.5 truncate flex-1">{status.webhook_url}</code>
              <Button variant="ghost" size="sm" className="h-6 px-1.5" onClick={() => copyText(status.webhook_url, "Webhook URL")}>
                <Copy className="h-3 w-3" />
              </Button>
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">
              This accepts ElevenLabs' own native post-call webhook directly — set your agent's "Post-call webhook" to this URL (must include the <code className="font-mono">/api</code> prefix exactly as shown — a very common cause of a webhook 404) and paste the signing secret ElevenLabs generates into the <b>ElevenLabs Post-Call Webhook Signing Secret</b> field above (verified via the <code className="font-mono">ElevenLabs-Signature</code> header, not an API key). Only the <code className="font-mono">post_call_transcription</code> and <code className="font-mono">call_initiation_failure</code> events are processed. We read <code className="font-mono">voice_attempt_id</code> back from <code className="font-mono">conversation_initiation_client_data.dynamic_variables</code> to identify the customer.
            </p>
          </div>
          <div>
            <p className="text-xs font-medium">Current Data Collection contract (configure these exact field IDs on your agent)</p>
            <p className="text-[11px] text-muted-foreground mt-1">
              <code className="font-mono">outcome</code> — one of <code className="font-mono">booked | consultation_requested | callback_requested | human_requested | declined | no_answer | failed</code>; <code className="font-mono">preferred_consultation_date</code> / <code className="font-mono">preferred_consultation_time</code> (IST, only when outcome is <code className="font-mono">booked</code> or <code className="font-mono">consultation_requested</code> — an exact time never auto-books by itself, only <code className="font-mono">outcome=booked</code> does); <code className="font-mono">decline_reason</code>; <code className="font-mono">transcript_summary</code>; <code className="font-mono">primary_objection</code>; <code className="font-mono">lead_temperature</code>; <code className="font-mono">human_followup_context</code>; <code className="font-mono">human_requested</code> (boolean — true ONLY when the customer explicitly asked for a person, never merely because the call was unclear); <code className="font-mono">callback_requested</code> (boolean); <code className="font-mono">preferred_callback_date</code>; <code className="font-mono">preferred_callback_time</code> (only an exact stated time — never fabricate one from a broad preference); <code className="font-mono">preferred_callback_time_window</code> (morning/afternoon/evening/night); <code className="font-mono">callback_by</code> (<code className="font-mono">AI | HUMAN | UNSPECIFIED</code>).
            </p>
            <p className="text-[11px] text-muted-foreground mt-1">
              Legacy field IDs (<code className="font-mono">preferred_date</code>, <code className="font-mono">preferred_time</code>, <code className="font-mono">reason</code>) are still read as a fallback if the current identifiers above are absent, but a newly-configured agent should use the current identifiers directly. The customer's response is always logged as a note; a non-booked, non-callback outcome always creates a human follow-up task; a genuinely ambiguous-but-connected call is flagged for human review rather than guessed at; and repeat deliveries of the same call outcome are ignored rather than duplicated.
            </p>
          </div>
        </CardContent>
      </Card>

      <Card data-testid="voice-place-test-call-card">
        <CardHeader className="pb-2"><CardTitle className="text-sm font-heading">Place test call (Admin only)</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <p className="text-[11px] text-muted-foreground">
            Auto-creates an isolated <code className="font-mono">is_test_record</code> Contact + Opportunity from the
            fields below and fires exactly ONE Voice Attempt against it through the exact normal dispatcher — no
            separate telephony path. Requires "Voice test calls allowed" above to be on. SalesOS itself computes real
            availability (earliest date / open slots) for the chosen product at dispatch time — there is no field
            here to fabricate that.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-[11px]">Test destination phone number *</Label>
              <Input value={testCallForm.phone} onChange={setField("phone")} placeholder="98765 43210"
                     data-testid="voice-test-call-phone" />
            </div>
            <div className="space-y-1">
              <Label className="text-[11px]">Dummy customer name *</Label>
              <Input value={testCallForm.name} onChange={setField("name")} placeholder="Test Customer"
                     data-testid="voice-test-call-name" />
            </div>
            <div className="space-y-1">
              <Label className="text-[11px]">Email</Label>
              <Input value={testCallForm.email} onChange={setField("email")} placeholder="optional" />
            </div>
            <div className="space-y-1">
              <Label className="text-[11px]">City</Label>
              <Input value={testCallForm.city} onChange={setField("city")} placeholder="optional" />
            </div>
            <div className="space-y-1">
              <Label className="text-[11px]">Product *</Label>
              <Select value={testCallForm.product_code}
                      onValueChange={(v) => setTestCallForm((f) => ({ ...f, product_code: v }))}>
                <SelectTrigger data-testid="voice-test-call-product"><SelectValue placeholder="Choose a product" /></SelectTrigger>
                <SelectContent>
                  {products.map((p) => <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-[11px]">Test scenario label</Label>
              <Input value={testCallForm.scenario_label} onChange={setField("scenario_label")}
                     placeholder="e.g. booking happy path" data-testid="voice-test-call-scenario" />
            </div>
            <div className="space-y-1">
              <Label className="text-[11px]">Lead source</Label>
              <Input value={testCallForm.lead_source} onChange={setField("lead_source")} placeholder="optional" />
            </div>
            <div className="space-y-1">
              <Label className="text-[11px]">Lead campaign</Label>
              <Input value={testCallForm.lead_campaign} onChange={setField("lead_campaign")} placeholder="optional" />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-[11px]">Lead details / enquiry context</Label>
            <Textarea value={testCallForm.lead_details} onChange={setField("lead_details")} rows={2}
                      placeholder="optional — reaches the agent as lead_details" />
          </div>
          <Button size="sm" onClick={openConfirm} data-testid="voice-place-test-call-open">
            <PhoneCall className="h-3.5 w-3.5 mr-1.5" /> Place test call…
          </Button>
        </CardContent>
      </Card>

      {confirmOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
             role="alertdialog" aria-modal="true" data-testid="voice-test-call-confirm-dialog"
             onClick={() => { if (!placingCall) setConfirmOpen(false); }}>
          <div className="bg-background border rounded-lg shadow-lg p-6 max-w-lg w-full space-y-4"
               onClick={(e) => e.stopPropagation()}>
            <div className="space-y-2 text-left">
              <h2 className="text-lg font-semibold text-[hsl(var(--destructive))]">TEST CALL — real telephone call</h2>
              <div className="text-sm text-muted-foreground space-y-2">
                <p>This will place a <strong className="text-foreground">real telephone call</strong> to the number entered below:</p>
                <p className="font-mono text-base text-foreground">{testCallForm.phone || "—"}</p>
                <p>as a dummy customer named <strong className="text-foreground">{testCallForm.name || "—"}</strong> for product{" "}
                  <strong className="text-foreground">{products.find((p) => p.code === testCallForm.product_code)?.name || testCallForm.product_code || "—"}</strong>.
                  A fresh, isolated test Contact and Opportunity will be created for this.</p>
              </div>
            </div>
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
              <Button variant="outline" disabled={placingCall} onClick={() => setConfirmOpen(false)}>Cancel</Button>
              <Button disabled={placingCall} onClick={placeTestCall} data-testid="voice-test-call-confirm">
                {placingCall ? "Placing call…" : "Confirm — place test call"}
              </Button>
            </div>
          </div>
        </div>
      )}

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm font-heading">Controlled test call — re-run against an existing test opportunity (Admin only)</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          <p className="text-[11px] text-muted-foreground">Fires exactly ONE Voice Attempt against a seeded TEST opportunity — never a real lead, never a batch. Requires the opportunity's own <code className="font-mono">is_test_record</code> to be true and "Voice test calls allowed" above to be on; the destination phone number always comes from that test record, never entered here. Use "Place test call" above instead if you don't already have a test opportunity ID.</p>
          <div className="flex items-center gap-2">
            <Input placeholder="Test opportunity ID" value={testOppId} onChange={(e) => setTestOppId(e.target.value)} className="h-8 max-w-sm" data-testid="voice-controlled-test-opp-id" />
            <Button size="sm" variant="outline" disabled={triggering} onClick={triggerControlledTest} data-testid="voice-controlled-test-trigger">
              {triggering ? "Queuing…" : "Run controlled test call"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card data-testid="voice-lease-diagnostics">
        <CardHeader className="pb-2"><CardTitle className="text-sm font-heading">Capacity lease & queue diagnostics</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
            <div><p className="text-muted-foreground">Concurrency</p><p className="font-semibold">{monitor.capacity_in_use}/{monitor.capacity_max} in use</p></div>
            <div><p className="text-muted-foreground">Effective stale lease timeout</p><p className="font-semibold">{monitor.effective_stale_timeout_minutes} min</p></div>
            <div><p className="text-muted-foreground">Effective capacity-wait requeue</p><p className="font-semibold">{monitor.effective_capacity_wait_minutes} min</p></div>
            <div><p className="text-muted-foreground">Next eligible attempt</p><p className="font-semibold">{monitor.next_eligible_attempt_at ? fmtIST(monitor.next_eligible_attempt_at, { zone: true }) : "—"}</p></div>
            <div><p className="text-muted-foreground">Pending queue (total unresolved)</p><p className="font-semibold">{monitor.pending_queue_depth}</p></div>
            <div><p className="text-muted-foreground">...waiting for capacity</p><p className="font-semibold">{monitor.pending_waiting_for_capacity}</p></div>
          </div>
          {monitor.lease_holders?.length > 0 && (
            <div className="rounded-md border p-2.5 text-xs space-y-1">
              <p className="font-medium text-muted-foreground">Current lease holder(s)</p>
              {monitor.lease_holders.map((l) => (
                <div key={l.voice_attempt_id} className="flex flex-wrap items-center gap-x-3 gap-y-0.5" data-testid={`voice-lease-${l.voice_attempt_id}`}>
                  <span className="font-mono">{l.voice_attempt_id.slice(0, 8)}…</span>
                  <span>attempt #{l.attempt_no ?? "?"}</span>
                  <span>outcome: <b>{l.outcome || "—"}</b></span>
                  <span>state: <b>{DISPATCH_STATE_LABELS[l.dispatch_state] || l.dispatch_state || "—"}</b></span>
                  <span className={l.lease_age_seconds > (monitor.effective_stale_timeout_minutes * 60) ? "text-[hsl(var(--warning))]" : ""}>
                    lease age: {fmtDur(l.lease_age_seconds)}
                  </span>
                  {!l.found && <span className="text-[hsl(var(--danger))]">orphaned — no matching voice_attempts record found</span>}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card data-testid="voice-sweep-health">
        <CardHeader className="pb-2"><CardTitle className="text-sm font-heading">Voice stale sweep health</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          <p className="text-[11px] text-muted-foreground">
            The background sweep that releases a stuck lease and reconciles a never-dispatched or
            result-missing attempt after the configured timeout. If this stops making progress, it now shows here
            instead of only being discoverable by a stuck lease age.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
            <div><p className="text-muted-foreground">Last run</p><p className="font-semibold">{sweepHealth.last_run_at ? fmtIST(sweepHealth.last_run_at, { zone: true }) : "never"}</p></div>
            <div><p className="text-muted-foreground">Last successful run</p><p className="font-semibold">{sweepHealth.last_success_at ? fmtIST(sweepHealth.last_success_at, { zone: true }) : "never"}</p></div>
            <div><p className="text-muted-foreground">Last failed run</p><p className={`font-semibold ${sweepHealth.last_failure_at ? "text-[hsl(var(--warning))]" : ""}`}>{sweepHealth.last_failure_at ? fmtIST(sweepHealth.last_failure_at, { zone: true }) : "none"}</p></div>
            <div><p className="text-muted-foreground">Attempt candidates examined</p><p className="font-semibold">{sweepHealth.last_inspected ?? "—"}</p></div>
            <div><p className="text-muted-foreground">Attempts reconciled</p><p className="font-semibold">{sweepHealth.last_recovered ?? "—"}</p></div>
            <div><p className="text-muted-foreground">Per-run errors</p><p className={`font-semibold ${sweepHealth.last_errors_count ? "text-[hsl(var(--warning))]" : ""}`}>{sweepHealth.last_errors_count ?? "—"}</p></div>
          </div>
          {/* 2026-09-IV audit — direct lease-holder inspection counters,
              distinct from the attempt-candidate counters above: this is
              what answers "did the sweep even LOOK at the current
              capacity holder", which the bulk-query counters alone could
              not (a genuinely 535-minute-stale lease could previously
              coexist with "Attempt candidates examined: 0"). */}
          <div className="rounded-md border p-2.5 text-xs space-y-1">
            <p className="font-medium text-muted-foreground">Direct capacity-lease inspection (Phase 1 — authoritative for capacity safety)</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div><p className="text-muted-foreground">Lease holders examined</p><p className="font-semibold">{sweepHealth.last_lease_holders_examined ?? "—"}</p></div>
              <div><p className="text-muted-foreground">Stale leases found</p><p className="font-semibold">{sweepHealth.last_lease_holders_stale ?? "—"}</p></div>
              <div><p className="text-muted-foreground">Stale leases released</p><p className="font-semibold">{sweepHealth.last_lease_holders_released ?? "—"}</p></div>
              <div><p className="text-muted-foreground">Orphan leases released</p><p className="font-semibold">{sweepHealth.last_lease_orphans_released ?? "—"}</p></div>
            </div>
          </div>
          {sweepHealth.last_error && (
            <div className="rounded-md border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 p-2 text-xs">
              <span className="font-medium">Last error: </span>
              <span className="font-mono break-all">{sweepHealth.last_error}</span>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2 flex-row items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-sm font-heading">Recent voice attempts</CardTitle>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer">
              <Checkbox checked={testOnly} onCheckedChange={(v) => setTestOnly(!!v)} data-testid="voice-monitor-test-only" />
              TEST only
            </label>
            <span className="text-xs text-muted-foreground">Concurrency: {monitor.capacity_in_use}/{monitor.capacity_max} in use</span>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Customer</TableHead><TableHead>Product</TableHead>
                <TableHead>Attempt</TableHead><TableHead>Type</TableHead>
                <TableHead>Status</TableHead><TableHead>Outcome</TableHead>
                <TableHead>Last dispatch</TableHead>
                <TableHead>Created (IST)</TableHead>
                <TableHead></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {visibleAttempts.length === 0 && (
                  <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground text-sm py-6">
                    {testOnly ? "No TEST voice attempts yet" : "No voice attempts yet"}
                  </TableCell></TableRow>
                )}
                {visibleAttempts.map((a) => (
                  <TableRow key={a.voice_attempt_id} data-testid={`voice-attempt-row-${a.voice_attempt_id}`}>
                    <TableCell className="text-xs">
                      {a.contact_name || "—"} {a.is_test_record && <Badge variant="outline" className="ml-1 text-[10px]">TEST</Badge>}
                      {a.test_scenario_label && <div className="text-muted-foreground">"{a.test_scenario_label}"{a.test_initiated_by_name && ` — ${a.test_initiated_by_name}`}</div>}
                    </TableCell>
                    <TableCell className="text-xs">{a.product || "—"}</TableCell>
                    <TableCell className="text-xs">#{a.attempt_no}{a.parent_attempt_id && <span className="text-muted-foreground"> (from {a.parent_attempt_id.slice(0, 8)}…)</span>}</TableCell>
                    <TableCell className="text-xs capitalize">{a.attempt_type}</TableCell>
                    <TableCell className="text-xs">{a.status}</TableCell>
                    <TableCell className="text-xs">
                      {a.outcome || "—"}
                      {a.booking_conflict_reason && <span className="text-[hsl(var(--warning))]"> ({a.booking_conflict_reason})</span>}
                      {a.callback_requested && <span className="text-muted-foreground"> · callback by {a.callback_by || "?"}</span>}
                      {a.human_requested && <span className="text-muted-foreground"> · human requested</span>}
                    </TableCell>
                    <TableCell className="text-xs max-w-[220px]">
                      {a.last_dispatch_status
                        ? <>
                            <div>{a.last_dispatch_status}{a.last_dispatch_attempts != null && ` (${a.last_dispatch_attempts}x)`}</div>
                            {a.provider_http_result && <div className="text-muted-foreground truncate" title={a.provider_http_result}>{a.provider_http_result}</div>}
                          </>
                        : <span className="text-muted-foreground">never dispatched</span>}
                    </TableCell>
                    <TableCell className="text-xs">{fmtIST(a.created_at)}</TableCell>
                    <TableCell className="text-xs">
                      <Button size="sm" variant="ghost" className="h-6 px-2"
                              onClick={() => setDetailAttempt(a)} data-testid={`voice-attempt-details-${a.voice_attempt_id}`}>
                        <Info className="h-3.5 w-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {detailAttempt && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
             role="dialog" aria-modal="true" data-testid="voice-attempt-detail-dialog"
             onClick={() => setDetailAttempt(null)}>
          <div className="bg-background border rounded-lg shadow-lg p-6 max-w-xl w-full space-y-3 max-h-[85vh] overflow-y-auto"
               onClick={(e) => e.stopPropagation()}>
            <h2 className="text-sm font-heading font-semibold">
              Attempt #{detailAttempt.attempt_no} — {detailAttempt.contact_name || "—"}
            </h2>
            <p className="text-[11px] text-muted-foreground">
              Full pre-dispatch/dispatch/resolution lifecycle for this attempt, answering exactly why it did or
              didn't reach the provider. Fields are blank ("—") for attempts created before this diagnostics fix
              shipped.
            </p>
            <div>
              <DetailField label="Dispatch state"
                value={DISPATCH_STATE_LABELS[detailAttempt.dispatch_state] || detailAttempt.dispatch_state} />
              <DetailField label="Voice attempt ID" value={detailAttempt.voice_attempt_id} />
              <DetailField label="Opportunity ID" value={detailAttempt.opportunity_id} />
              <DetailFieldTime label="Created" iso={detailAttempt.created_at} />
              <DetailFieldTime label="Lease acquired at" iso={detailAttempt.lease_acquired_at} />
              <DetailFieldTime label="Dispatch started at" iso={detailAttempt.dispatch_started_at} />
              <DetailField label="Provider HTTP status" value={detailAttempt.provider_http_status} />
              <DetailField label="Provider response/reference ID" value={detailAttempt.provider_ref} />
              <DetailFieldTime label="Initiated at" iso={detailAttempt.initiated_at} />
              <DetailFieldTime label="Last webhook received at" iso={detailAttempt.last_webhook_received_at} />
              <DetailFieldTime label="Next eligible at" iso={detailAttempt.next_eligible_at} />
              <DetailFieldTime label="Expected stale deadline" iso={detailAttempt.stale_deadline_at} />
              <DetailField label="Currently holding capacity?" value={detailAttempt.holding_capacity ? "Yes" : "No"} />
              <DetailField label="Stale-sweep classification" value={staleClassificationLabel(detailAttempt.stale_sweep_classification)} />
              <DetailField label="Enquiry context given to the agent" value={leadContextLabel(detailAttempt.lead_context, detailAttempt)} />
              <DetailField label="Availability given to the agent" value={availabilityContextLabel(detailAttempt.availability_context, detailAttempt)} />
              <DetailFieldTime label="Resolved at" iso={detailAttempt.resolved_at} />
              <DetailField label="Failure stage"
                value={FAILURE_STAGE_LABELS[detailAttempt.failure_stage] || detailAttempt.failure_stage} />
              <DetailField label="Failure reason" value={detailAttempt.failure_reason} />
              <DetailField label="Pre-dispatch retry count" value={detailAttempt.pre_dispatch_failure_count} />
              <DetailField label="Last error" value={detailAttempt.last_error} />
            </div>
            <div className="flex justify-end">
              <Button size="sm" variant="outline" onClick={() => setDetailAttempt(null)}>Close</Button>
            </div>
          </div>
        </div>
      )}

      <Card data-testid="voice-recovery-card">
        <CardHeader className="pb-2"><CardTitle className="text-sm font-heading">Pre-dispatch failure recovery (2026-09)</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <p className="text-[11px] text-muted-foreground">
            Finds historical VoiceAttempts that were marked customer-facing "failed" by the pre-fix stale sweep even
            though the outbound request to ElevenLabs was never actually sent — confirmed by strong evidence (no
            provider reference, no conversation ID, never reached "initiated"). Preview only lists candidates;
            nothing is changed until you select rows below and click Recover. Recovery never overwrites the original
            failure evidence, never consumes the customer's genuine attempt budget, and requeues through the normal,
            capacity-gated, calling-window-respecting dispatcher — never a direct call.
          </p>
          <Button size="sm" variant="outline" disabled={loadingPreview} onClick={loadRecoveryPreview}
                  data-testid="voice-recovery-preview-btn">
            {loadingPreview ? "Scanning…" : "Preview affected attempts"}
          </Button>
          {recoveryPreview && (
            <div className="space-y-2">
              <p className="text-xs">
                {recoveryPreview.total_count} candidate(s) found — <b>{recoveryPreview.eligible_count}</b> eligible
                for recovery.
              </p>
              {recoveryPreview.total_count > 0 && (
                <div className="overflow-x-auto rounded-md border">
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead></TableHead><TableHead>Customer</TableHead><TableHead>Product</TableHead>
                      <TableHead>Attempt</TableHead><TableHead>Created (IST)</TableHead>
                      <TableHead>Genuine dispatches</TableHead><TableHead>Eligible?</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {recoveryPreview.candidates.map((c) => (
                        <TableRow key={c.voice_attempt_id} data-testid={`voice-recovery-row-${c.voice_attempt_id}`}>
                          <TableCell>
                            <Checkbox checked={!!selectedRecovery[c.voice_attempt_id]} disabled={!c.eligible}
                                      onCheckedChange={() => toggleRecoverySelection(c.voice_attempt_id)}
                                      data-testid={`voice-recovery-select-${c.voice_attempt_id}`} />
                          </TableCell>
                          <TableCell className="text-xs">{c.contact_name || "—"}</TableCell>
                          <TableCell className="text-xs">{c.product || "—"}</TableCell>
                          <TableCell className="text-xs">#{c.attempt_no}</TableCell>
                          <TableCell className="text-xs">{fmtIST(c.created_at)}</TableCell>
                          <TableCell className="text-xs">{c.genuine_dispatches_so_far}/{c.max_attempts}</TableCell>
                          <TableCell className="text-xs">
                            {c.eligible
                              ? <span className="text-[hsl(var(--success))]">Yes</span>
                              : <span className="text-muted-foreground" title={c.ineligible_reasons.join("; ")}>
                                  No — {c.ineligible_reasons[0]}
                                </span>}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
              {recoveryPreview.eligible_count > 0 && (
                <Button size="sm" disabled={executingRecovery} onClick={executeRecovery}
                        data-testid="voice-recovery-execute-btn">
                  {executingRecovery ? "Recovering…" : `Recover ${Object.values(selectedRecovery).filter(Boolean).length} selected`}
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card data-testid="voice-race-preview-card">
        <CardHeader className="pb-2"><CardTitle className="text-sm font-heading">Historical Voice race preview (2026-09, Round 12)</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <p className="text-[11px] text-muted-foreground">
            Read-only scan for the 9 historical dispatch-race/attempt-policy anomaly shapes (reuses the same
            scanner as the recovery tool above — never a second classifier). Nothing here is ever applied
            automatically; every finding states whether it is safe for an automatic field correction or needs
            manual review, and repairing anything found here is a separate action outside this preview entirely.
          </p>
          <Button size="sm" variant="outline" disabled={loadingRacePreview} onClick={loadRacePreview}
                  data-testid="voice-race-preview-btn">
            {loadingRacePreview ? "Scanning…" : "Run historical race preview"}
          </Button>
          {racePreview && (
            <div className="space-y-3">
              <div className={`rounded-md border p-2.5 text-xs ${racePreview.pratik_saini_reproduction?.found ? "border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10" : ""}`}
                   data-testid="voice-race-pratik-status">
                <p className="font-medium">Pratik Saini reproduction check</p>
                <p className="text-muted-foreground mt-0.5">{racePreview.pratik_saini_reproduction?.note}</p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
                {Object.entries(racePreview.summary || {}).map(([key, s]) => (
                  <div key={key} className="rounded-md border p-2">
                    <p className="text-muted-foreground">{CONDITION_LABELS[key] || key}</p>
                    <p className="font-semibold">{s.event_count} events</p>
                    <p>{s.unique_customer_count} customers · {s.unique_opportunity_count} opportunities · {s.unique_voice_attempt_id_count} attempts</p>
                  </div>
                ))}
              </div>
              {Object.entries(racePreview.conditions || {}).map(([condKey, condition]) => (
                <section key={condKey} className="space-y-2" data-testid={`voice-race-condition-${condKey}`}>
                  <p className="text-xs text-muted-foreground" data-testid={`voice-race-range-${condKey}`}>
                    {CONDITION_LABELS[condKey] || condKey}: showing {condition.shown} of {condition.total_groups} customer groups
                    {" "}(page {condition.page} of {condition.total_pages}). Event totals include records outside the display window.
                  </p>
                  {condition.total_pages > 1 && (
                    <div className="flex items-center gap-2 text-xs" data-testid={`voice-race-pager-${condKey}`}>
                      <Button type="button" size="sm" variant="outline" className="h-7 text-xs" data-testid={`voice-race-prev-${condKey}`}
                              disabled={condition.page <= 1 || !!racePageBusy[condKey]} onClick={() => loadRacePage(condKey, condition.page - 1)}>Previous</Button>
                      <span data-testid={`voice-race-page-${condKey}`}>Page {condition.page} of {condition.total_pages}</span>
                      <Button type="button" size="sm" variant="outline" className="h-7 text-xs" data-testid={`voice-race-next-${condKey}`}
                              disabled={condition.page >= condition.total_pages || !!racePageBusy[condKey]} onClick={() => loadRacePage(condKey, condition.page + 1)}>Next</Button>
                      {racePageBusy[condKey] && <span className="text-muted-foreground">Loading…</span>}
                    </div>
                  )}
                  {racePageError[condKey] && (
                    <p role="alert" data-testid={`voice-race-page-error-${condKey}`} className="text-xs text-[hsl(var(--danger))]">
                      {racePageError[condKey]} The page that was already showing is unchanged.
                    </p>
                  )}
                  {condition.findings.map((group, groupIndex) => (
                    <div key={group.customer?.customer_id || group.customer?.contact_id || groupIndex}
                         className="space-y-2 rounded-md border p-2" data-testid={`voice-race-group-${condKey}-${groupIndex}`}>
                      <p className="text-xs font-medium">
                        {group.customer?.full_name || "Unknown customer"} — {group.attempt_count} matching events
                        {group.manual_review_required ? " · Manual review required" : ""}
                      </p>
                      {group.matching_attempts.map((f, attemptIndex) => {
                        const i = `${groupIndex}-${attemptIndex}`;
                        return (
                  <div key={`${condKey}-${i}`} className="rounded-md border p-2.5 text-xs space-y-1"
                       data-testid={`voice-race-finding-${condKey}-${i}`}>
                    <div className="flex items-center justify-between flex-wrap gap-1">
                      <span className="font-medium">{CONDITION_LABELS[condKey] || condKey}</span>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className={f.safe_for_automatic_repair
                          ? "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/30"
                          : "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30"}>
                          {f.safe_for_automatic_repair ? "Deterministic — safe for automatic repair" : "Manual review required"}
                        </Badge>
                        <Button size="sm" variant="ghost" className="h-6 px-2"
                                onClick={() => setExpandedFinding(expandedFinding === `${condKey}-${i}` ? null : `${condKey}-${i}`)}>
                          {expandedFinding === `${condKey}-${i}` ? "Hide" : "Details"}
                        </Button>
                      </div>
                    </div>
                    <div>{f.customer?.full_name || "—"} {f.customer?.customer_id && <span className="font-mono text-muted-foreground">({f.customer.customer_id})</span>} — opportunity <span className="font-mono">{f.opportunity?.opportunity_id}</span></div>
                    <div className="text-muted-foreground">{f.classification}</div>
                    {expandedFinding === `${condKey}-${i}` && (
                      <div className="space-y-2 pt-1 border-t mt-1">
                        <div><span className="font-medium">Handover time: </span>{f.handover_timestamp ? fmtIST(f.handover_timestamp, { zone: true }) : "—"}</div>
                        <div><span className="font-medium">Current owner (human): </span>{f.ownership_and_visible_state?.current_owner_name || "Unassigned"}</div>
                        <div><span className="font-medium">Booking/voice state: </span>{f.ownership_and_visible_state?.derived_label || "—"} (owns_outreach: {String(f.ownership_and_visible_state?.owns_outreach)})</div>
                        <div><span className="font-medium">Provider contact evidence: </span>{f.provider_contact_evidence?.note}</div>
                        <div><span className="font-medium">Proposed repair: </span>{f.proposed_repair}</div>
                        <div><span className="font-medium">Reason: </span>{f.why_deterministic_or_manual}</div>
                        {f.proposed_field_corrections?.length > 0 && (
                          <div><span className="font-medium">Proposed field correction(s): </span>{f.proposed_field_corrections.map((c) => `${c.field} — ${c.note}`).join("; ")}</div>
                        )}
                        {f.proposed_task_treatment?.length > 0 && (
                          <div>
                            <span className="font-medium">Associated task treatment:</span>
                            <ul className="list-disc pl-4">
                              {f.proposed_task_treatment.map((t) => (
                                <li key={t.task_id}>{t.title} — <b>{t.proposed_action}</b>: {t.reason}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {f.timeline?.length > 0 && (
                          <div>
                            <span className="font-medium">Timeline:</span>
                            <ul className="list-disc pl-4">
                              {f.timeline.map((e, ti) => (
                                <li key={ti}>{fmtIST(e.at, { zone: true })} — {e.label}{e.actor ? ` (${e.actor})` : ""}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                        );
                      })}
                    </div>
                  ))}
                </section>
              ))}
              {Object.values(racePreview.summary || {}).every((s) => s.event_count === 0) && (
                <p className="text-xs text-muted-foreground">No anomalies matched any of the 9 conditions in this database.</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
